<script setup lang="ts">
/**
 * SpanInput — a length of time as a count and a unit. The model is the string
 * a time filter stores ("90 days", "1 week"); an unreadable or empty model
 * shows an empty count.
 */
import { ref, watch } from "vue"

const UNITS = ["minute", "hour", "day", "week", "month", "year"] as const
type Unit = (typeof UNITS)[number]

const model = defineModel<string | null>()

function read(value: string | null | undefined): { n: number | null; unit: Unit } {
  const match =
    typeof value === "string" ? value.match(/^(\d+) (minute|hour|day|week|month|year)s?$/) : null
  return match ? { n: Number(match[1]), unit: match[2] as Unit } : { n: null, unit: "day" }
}

const count = ref(read(model.value).n)
const unit = ref(read(model.value).unit)

watch(model, (value) => {
  const next = read(value)
  if (value) {
    count.value = next.n
    unit.value = next.unit
  }
})

function write() {
  model.value =
    count.value == null ? "" : `${count.value} ${unit.value}${count.value === 1 ? "" : "s"}`
}

function setCount(raw: string) {
  count.value = raw === "" ? null : Math.max(0, Math.trunc(Number(raw)))
  write()
}

function setUnit(next: string) {
  unit.value = next as Unit
  write()
}
</script>

<template>
  <div class="span-input flex items-center g-2">
    <input
      type="number"
      min="1"
      step="1"
      class="input is-small"
      :value="count ?? ''"
      @input="setCount(($event.target as HTMLInputElement).value)"
    />
    <div class="select is-small">
      <select :value="unit" @change="setUnit(($event.target as HTMLSelectElement).value)">
        <option v-for="u in UNITS" :key="u" :value="u">{{ u }}{{ count === 1 ? "" : "s" }}</option>
      </select>
    </div>
  </div>
</template>

<style>
.span-input {
  input {
    max-width: 5rem;
  }
}
</style>
